/**
 * Theme and density.
 *
 * The initial value is applied by an inline script in `index.html` before first
 * paint, so there is never a flash of the wrong theme. This module only handles
 * changes made after the app has loaded.
 */

export type Theme = "system" | "light" | "dark";
export type Density = "comfortable" | "compact";

const THEME_KEY = "fc-theme";
const DENSITY_KEY = "fc-density";

const media =
  typeof window !== "undefined"
    ? window.matchMedia("(prefers-color-scheme: light)")
    : null;

function resolve(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  return media?.matches ? "light" : "dark";
}

export function getTheme(): Theme {
  const stored = localStorage.getItem(THEME_KEY);
  return stored === "light" || stored === "dark" ? stored : "system";
}

export function setTheme(theme: Theme): void {
  if (theme === "system") localStorage.removeItem(THEME_KEY);
  else localStorage.setItem(THEME_KEY, theme);

  document.documentElement.dataset.theme = resolve(theme);
  // Keeps the browser chrome (address bar, form controls) in step.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute(
      "content",
      resolve(theme) === "light" ? "#FAFAF9" : "#0C0C10",
    );
}

export function getDensity(): Density {
  return localStorage.getItem(DENSITY_KEY) === "compact"
    ? "compact"
    : "comfortable";
}

export function setDensity(density: Density): void {
  localStorage.setItem(DENSITY_KEY, density);
  document.documentElement.dataset.density = density;
}

/** Follow the OS while the user's preference is "system". */
export function watchSystemTheme(): () => void {
  const handler = () => {
    if (getTheme() === "system") setTheme("system");
  };
  media?.addEventListener("change", handler);
  return () => media?.removeEventListener("change", handler);
}
